import {normalizeInput, stepMovement} from './movement.mjs';
import {stepPortalCrossing} from './portal-math.mjs';
const at=(worlds,id)=>worlds instanceof Map?worlds.get(id):worlds?.[id];
const frameMatches=(a,b)=>a.worldId===b.worldId&&(a.crossingSeq??0)===(b.crossingSeq??0);

/** Predict the exact ordered, frame-tagged samples the authority validates. A
 * doorway is part of simulation, not a delayed HTTP animation. Reconciliation
 * replays through cached worlds and reports only the net camera-frame change.
 */
export function createMovementPrediction({maxPending = 512, resolveMovement = (_previous,moved) => moved, captureMovementConstraint = () => undefined} = {}) {
  let pending = [], acknowledged = -1, heading = 0, headingDelta = 0;
  const crossingHeadings=new Map();
  function step(player,sample,world,worlds,constraint){
    const moved=resolveMovement(player,stepMovement(player,sample,sample.dt,world),sample.dt,world,constraint);
    if(!worlds)return {player:moved,crossing:null};
    const result=stepPortalCrossing(player,moved,world,worlds,sample.dt,{preparedOnly:true,blockedPortalWorldIds:sample.blockedPortalWorldIds});
    if(result.crossing){
      result.player.crossingSeq=(player.crossingSeq??0)+1;
      crossingHeadings.set(result.player.crossingSeq,result.crossing.headingDelta);
      if(crossingHeadings.size>64)crossingHeadings.delete(crossingHeadings.keys().next().value);
    }
    return result;
  }
  return {
    record(player, input, dt, world, worlds) {
      const sample = {type:'input',...(worlds?{predictPortals:true}:{}),worldId:world.id,crossingSeq:player.crossingSeq??0,...normalizeInput(input), dt: Math.min(.055, Math.max(0, Number.isFinite(dt) ? dt : 0))};
      const constraint=captureMovementConstraint(player,world),beforeHeading=heading,result=step(player,sample,world,worlds,constraint);
      headingDelta=result.crossing?.headingDelta??0;heading+=headingDelta;
      pending.push({sample,constraint,beforeHeading,afterHeading:heading,before:{worldId:player.worldId,crossingSeq:player.crossingSeq??0},after:{worldId:result.player.worldId,crossingSeq:result.player.crossingSeq??0}});
      if (pending.length > maxPending) pending.splice(0, pending.length - maxPending);
      return {...result,sample};
    },
    reconcile(player, authoritative, world, {reset = false, worlds} = {}) {
      headingDelta=0;
      const ack = authoritative.lastInputSeq ?? 0;
      if (ack < acknowledged && !reset) return player;
      const entry=pending.find(entry=>entry.sample.seq===ack);
      let baseHeading=heading;
      if(entry){
        if(frameMatches(entry.after,authoritative))baseHeading=entry.afterHeading;
        else {
          baseHeading=pending.findLast(candidate=>candidate.sample.seq<=ack&&frameMatches(candidate.before,authoritative))?.beforeHeading??entry.beforeHeading;
          if((authoritative.crossingSeq??0)===entry.before.crossingSeq+1&&authoritative.lastCrossing?.fromWorldId===entry.before.worldId)baseHeading+=authoritative.lastCrossing.headingDelta??0;
        }
      } else if(pending.length)baseHeading=pending[0].beforeHeading;
      else if(!frameMatches(player,authoritative)&&authoritative.lastCrossing?.seq===(player.crossingSeq??0)+1)baseHeading+=authoritative.lastCrossing.headingDelta??0;
      const resetRollback=reset&&(authoritative.crossingSeq??0)<(player.crossingSeq??0);
      if(resetRollback){
        // Frame history can be cropped during a long outage. Portal frame
        // deltas outlive those samples so an authority rebase still undoes them.
        baseHeading=heading;
        for(const [sequence,delta] of crossingHeadings)if(sequence>(authoritative.crossingSeq??0)&&sequence<=(player.crossingSeq??0))baseHeading-=delta;
      }
      const canonicalCrossing=authoritative.lastCrossing,predictedHeading=crossingHeadings.get(canonicalCrossing?.seq);
      if((resetRollback||!entry||frameMatches(entry.after,authoritative))&&Number.isFinite(predictedHeading)&&Number.isFinite(canonicalCrossing?.headingDelta)&&canonicalCrossing.seq===(authoritative.crossingSeq??0)){
        baseHeading+=canonicalCrossing.headingDelta-predictedHeading;
        crossingHeadings.set(canonicalCrossing.seq,canonicalCrossing.headingDelta);
      }
      acknowledged = ack;
      pending = reset ? [] : pending.filter(entry => entry.sample.seq > ack);
      let result = {...player, ...authoritative, position: {...authoritative.position},activePortalId:authoritative.activePortalId??null,lastPortalId:authoritative.lastPortalId??null,portalCooldownRemaining:authoritative.portalCooldownRemaining??0,crossingSeq:authoritative.crossingSeq??0,portalTraversal:authoritative.portalTraversal??null};
      if(!Object.hasOwn(authoritative,'portalTraversal'))delete result.portalTraversal;
      for (const entry of pending) {
        const currentWorld=at(worlds,result.worldId)??(world.id===result.worldId?world:null);
        // Never interpret input axes from an unconfirmed, rejected portal frame.
        if(!currentWorld||!frameMatches(entry.sample,result))break;
        entry.beforeHeading=baseHeading;
        const replay=step(result,entry.sample,currentWorld,worlds,entry.constraint);result=replay.player;
        baseHeading+=replay.crossing?.headingDelta??0;entry.afterHeading=baseHeading;
        entry.after={worldId:result.worldId,crossingSeq:result.crossingSeq??0};
      }
      headingDelta=baseHeading-heading;heading=baseHeading;
      return result;
    },
    reset() { pending = []; acknowledged = -1; heading=0;headingDelta=0;crossingHeadings.clear(); },
    get headingDelta(){return headingDelta;},
    get pendingCount() { return pending.length; },
  };
}

